import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { CheckCircle, AlertCircle, Globe } from 'lucide-react';

const FORM_CONFIGS = {
  qc: {
    title: { en: 'QC Investigation Form', ar: 'نموذج تحقيق مراقبة الجودة' },
    color: 'purple',
    fields: [
      { key: 'investigator_name', label: { en: 'Investigator Name', ar: 'اسم المحقق' }, type: 'text', required: true },
      { key: 'investigation_date', label: { en: 'Investigation Date', ar: 'تاريخ التحقيق' }, type: 'date', required: true },
      { key: 'root_cause', label: { en: 'Root Cause', ar: 'السبب الجذري' }, type: 'textarea', required: true },
      { key: 'findings', label: { en: 'Investigation Findings', ar: 'نتائج التحقيق' }, type: 'textarea', required: true },
      { key: 'corrective_action', label: { en: 'Corrective Action Taken', ar: 'الإجراء التصحيحي المتخذ' }, type: 'textarea', required: true },
      { key: 'sample_batch_number', label: { en: 'Sample/Batch Number', ar: 'رقم العينة/الدفعة' }, type: 'text' },
      { key: 'contamination_type', label: { en: 'Contamination Type (if applicable)', ar: 'نوع التلوث (إن وجد)' }, type: 'text' },
      { key: 'recommendation', label: { en: 'Recommendation', ar: 'التوصية' }, type: 'textarea' },
    ]
  },
  finance: {
    title: { en: 'Finance Refund Form', ar: 'نموذج استرداد المالية' },
    color: 'green',
    fields: [
      { key: 'finance_officer', label: { en: 'Finance Officer Name', ar: 'اسم المسؤول المالي' }, type: 'text', required: true },
      { key: 'refund_amount', label: { en: 'Refund Amount (KD)', ar: 'مبلغ الاسترداد (د.ك)' }, type: 'number', required: true },
      { key: 'auth_code', label: { en: 'Authorization Code', ar: 'رمز التفويض' }, type: 'text', required: true },
      { key: 'payment_method', label: { en: 'Payment Method', ar: 'طريقة الدفع' }, type: 'select', options: ['KNET', 'Credit Card', 'Cash', 'Bank Transfer', 'Voucher'], required: true },
      { key: 'transaction_date', label: { en: 'Transaction Date', ar: 'تاريخ المعاملة' }, type: 'date', required: true },
      { key: 'transaction_reference', label: { en: 'Transaction Reference', ar: 'المرجع' }, type: 'text' },
      { key: 'notes', label: { en: 'Finance Notes', ar: 'ملاحظات المالية' }, type: 'textarea' },
    ]
  },
  dietitian: {
    title: { en: 'Dietitian Assessment Form', ar: 'نموذج تقييم أخصائي التغذية' },
    color: 'blue',
    fields: [
      { key: 'dietitian_name', label: { en: 'Dietitian Name', ar: 'اسم أخصائي التغذية' }, type: 'text', required: true },
      { key: 'assessment_date', label: { en: 'Assessment Date', ar: 'تاريخ التقييم' }, type: 'date', required: true },
      { key: 'customer_dietary_plan', label: { en: 'Customer Dietary Plan', ar: 'خطة النظام الغذائي للعميل' }, type: 'text', required: true },
      { key: 'meal_analysis', label: { en: 'Meal Analysis', ar: 'تحليل الوجبة' }, type: 'textarea', required: true },
      { key: 'allergen_check', label: { en: 'Allergen Assessment', ar: 'تقييم مسببات الحساسية' }, type: 'textarea', required: true },
      { key: 'nutritional_deviation', label: { en: 'Nutritional Deviation Found', ar: 'الانحراف الغذائي المكتشف' }, type: 'select', options: ['Yes - Minor', 'Yes - Major', 'No deviation found'], required: true },
      { key: 'corrective_recommendation', label: { en: 'Corrective Recommendation', ar: 'التوصية التصحيحية' }, type: 'textarea', required: true },
      { key: 'follow_up_required', label: { en: 'Follow-up Required', ar: 'متابعة مطلوبة' }, type: 'select', options: ['Yes', 'No'] },
      { key: 'additional_notes', label: { en: 'Additional Notes', ar: 'ملاحظات إضافية' }, type: 'textarea' },
    ]
  },
  logistics: {
    title: { en: 'Logistics Delivery Confirmation', ar: 'نموذج تأكيد التسليم من اللوجستيات' },
    color: 'orange',
    fields: [
      { key: 'driver_name', label: { en: 'Driver Name', ar: 'اسم السائق' }, type: 'text', required: true },
      { key: 'driver_id', label: { en: 'Driver ID', ar: 'رقم هوية السائق' }, type: 'text', required: true },
      { key: 'delivery_date', label: { en: 'Delivery Date', ar: 'تاريخ التسليم' }, type: 'date', required: true },
      { key: 'delivery_time', label: { en: 'Delivery Time', ar: 'وقت التسليم' }, type: 'time' },
      { key: 'delivery_status', label: { en: 'Delivery Status', ar: 'حالة التسليم' }, type: 'select', options: ['Delivered on time', 'Delivered late', 'Not delivered - customer absent', 'Not delivered - wrong address', 'Partial delivery', 'Other'], required: true },
      { key: 'delivery_location', label: { en: 'Delivery Location/Area', ar: 'موقع/منطقة التسليم' }, type: 'text', required: true },
      { key: 'gps_confirmation', label: { en: 'GPS Tracking Reference', ar: 'مرجع تتبع GPS' }, type: 'text' },
      { key: 'recipient_name', label: { en: 'Recipient Name', ar: 'اسم المستلم' }, type: 'text' },
      { key: 'delivery_notes', label: { en: 'Delivery Notes', ar: 'ملاحظات التسليم' }, type: 'textarea' },
      { key: 'incident_description', label: { en: 'Incident Description (if any)', ar: 'وصف الحادث (إن وجد)' }, type: 'textarea' },
    ]
  }
};

const COLOR_MAP = {
  purple: { header: 'bg-purple-600', ring: 'focus:ring-purple-500', btn: 'bg-purple-600 hover:bg-purple-700', light: 'bg-purple-50 border-purple-200 text-purple-700' },
  green: { header: 'bg-green-600', ring: 'focus:ring-green-500', btn: 'bg-green-600 hover:bg-green-700', light: 'bg-green-50 border-green-200 text-green-700' },
  blue: { header: 'bg-blue-600', ring: 'focus:ring-blue-500', btn: 'bg-blue-600 hover:bg-blue-700', light: 'bg-blue-50 border-blue-200 text-blue-700' },
  orange: { header: 'bg-orange-600', ring: 'focus:ring-orange-500', btn: 'bg-orange-600 hover:bg-orange-700', light: 'bg-orange-50 border-orange-200 text-orange-700' },
};

export default function ThirdPartyForm({ formType }) {
  const { ticketId } = useParams();
  const [lang, setLang] = useState('en');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ submitted_by_email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const config = FORM_CONFIGS[formType];
  const colors = COLOR_MAP[config?.color || 'blue'];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const t = (field) => typeof field === 'object' ? field[lang] : field;

  useEffect(() => {
    api.get(`/third-party/ticket/${ticketId}`)
      .then(r => setTicket(r.data))
      .catch(() => setError('Ticket not found'))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const missing = config.fields.filter(f => f.required && !form[f.key]);
    if (missing.length > 0) {
      setError(`Please fill in all required fields: ${missing.map(f => t(f.label)).join(', ')}`);
      return;
    }
    if (!form.submitted_by_email) {
      setError('Please provide your email address');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await api.post(`/third-party/${formType}/${ticketId}`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir={dir}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {lang === 'ar' ? 'تم الإرسال بنجاح' : 'Submission Received'}
          </h2>
          <p className="text-gray-500 text-sm">
            {lang === 'ar'
              ? `شكراً لك. تم استلام تقريرك للتذكرة رقم ${ticket?.ticket_number}.`
              : `Thank you. Your report for ticket ${ticket?.ticket_number} has been received.`}
          </p>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={dir}>
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className={`${colors.header} text-white px-6 py-8`}>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold">{t(config.title)}</h1>
                <p className="text-white/80 text-sm mt-1">
                  {lang === 'ar' ? 'تذكرة رقم' : 'Ticket'}: {ticket?.ticket_number}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')}
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
              >
                <Globe className="w-4 h-4" />
                {lang === 'en' ? 'العربية' : 'English'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Ticket Summary */}
          <div className={`border rounded-xl p-4 ${colors.light}`}>
            <h3 className="font-semibold text-sm mb-2">
              {lang === 'ar' ? 'ملخص التذكرة' : 'Ticket Summary'}
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-xs opacity-70">{lang === 'ar' ? 'الموضوع' : 'Subject'}</span>
                <p className="font-medium">{ticket?.subject}</p>
              </div>
              <div>
                <span className="text-xs opacity-70">{lang === 'ar' ? 'النوع' : 'Type'}</span>
                <p className="font-medium">{ticket?.complaint_type || '—'}</p>
              </div>
              <div>
                <span className="text-xs opacity-70">{lang === 'ar' ? 'العميل' : 'Customer'}</span>
                <p className="font-medium">{ticket?.customer_name || '—'}</p>
              </div>
              <div>
                <span className="text-xs opacity-70">{lang === 'ar' ? 'الشركة' : 'Entity'}</span>
                <p className="font-medium">{ticket?.entity_name}</p>
              </div>
            </div>

            {/* Complaint Photos */}
            {ticket?.complaint_photo && (() => {
              const photos = ticket.complaint_photo.split(',').map(p => p.trim()).filter(Boolean);
              if (photos.length === 0) return null;
              return (
                <div className="mt-3 pt-3 border-t border-current border-opacity-20">
                  <p className="text-xs font-semibold opacity-70 mb-2">
                    {lang === 'ar' ? 'صور الشكوى' : 'Complaint Photos'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {photos.map((p, i) => {
                      const filename = p.replace(/^\/uploads\//, '');
                      const src = `http://56.228.22.103/uploads/${filename}`;
                      return (
                        <a key={i} href={src} target="_blank" rel="noreferrer">
                          <img
                            src={src}
                            alt={`Photo ${i + 1}`}
                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.1)' }}
                          />
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Form Fields */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
            <h3 className="font-semibold text-gray-900">
              {lang === 'ar' ? 'تفاصيل التقرير' : 'Report Details'}
            </h3>

            {/* Submitter Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {lang === 'ar' ? 'بريدك الإلكتروني *' : 'Your Email Address *'}
              </label>
              <input
                type="email"
                value={form.submitted_by_email}
                onChange={e => set('submitted_by_email', e.target.value)}
                required
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${colors.ring}`}
                placeholder="your@email.com"
              />
            </div>

            {config.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t(field.label)}{field.required && ' *'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={form[field.key] || ''}
                    onChange={e => set(field.key, e.target.value)}
                    required={field.required}
                    rows={3}
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${colors.ring} resize-none`}
                  />
                ) : field.type === 'select' ? (
                  <select
                    value={form[field.key] || ''}
                    onChange={e => set(field.key, e.target.value)}
                    required={field.required}
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${colors.ring} bg-white`}
                  >
                    <option value="">— {lang === 'ar' ? 'اختر' : 'Select'} —</option>
                    {field.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    value={form[field.key] || ''}
                    onChange={e => set(field.key, e.target.value)}
                    required={field.required}
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${colors.ring}`}
                  />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={`w-full ${colors.btn} disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors text-sm`}
          >
            {submitting
              ? (lang === 'ar' ? 'جاري الإرسال...' : 'Submitting...')
              : (lang === 'ar' ? 'إرسال التقرير' : 'Submit Report')}
          </button>

          <p className="text-xs text-gray-400 text-center">
            {lang === 'ar' ? 'هذا النموذج للاستخدام الداخلي فقط' : 'This form is for internal use only — Aralsane Group'}
          </p>
        </div>
      </form>
    </div>
  );
}
